import React from 'react';
import { usePageContainerBySlugQuery } from '../../../features/custom-page/WebsiteEditorCustomPage.graphql.generated';
import { useAccommodationHotelsQuery } from '../Accommodations.graphql.generated';
import { AccommodationsContext } from './Accommodations.context';
import type { TravelMapComponentFragment } from '../../../features/custom-page/WebsiteEditorCustomPage.graphql.generated';
import { useAuth } from '@shared/components/AuthProvider';
import { useEventPlaces } from '@shared/utils/eventPlace';
import { useEventInfo } from '@shared/utils/eventInfo';
import type { EditorPageConfig } from '@website-editor/WebsiteEditor.types';

type AccommodationsProviderProps = Readonly<{
  pageSlug: string;
  containerId: string;
  eventHandle: string;
  children: React.ReactNode;
  pageConfig: EditorPageConfig;
  componentData: TravelMapComponentFragment;
}>;

export const AccommodationsProvider: React.FC<AccommodationsProviderProps> = ({ children, componentData, eventHandle, containerId, pageSlug, pageConfig }) => {
  const { venueInfo, loading: loadingEventPlaces } = useEventPlaces();
  const { eventInfo } = useEventInfo();
  const { eventId } = eventInfo || {};

  const { isSuperAdmin } = useAuth();
  const { data: accommodationsData, loading: loadingAccommodations } = useAccommodationHotelsQuery({
    variables: { name: eventHandle || '' },
    batchMode: 'fast',
    // Always revalidate: EventAccommodationsCheck on the dashboard caches accommodationPlacesV2 on the same normalized Event entity, causing cache-first to return stale data on SPA navigation.
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  const { data: pageContainerData, refetch: refetchPageContainer } = usePageContainerBySlugQuery({
    skip: !eventId,
    batchMode: 'fast',
    fetchPolicy: 'cache-only',
    nextFetchPolicy: 'cache-only',
    variables: {
      eventId: eventId!,
      payload: { eventName: eventHandle, pageSlug },
      // Accommodations is a blocked, never-migrated slug, so it can't be contested by a
      // userGenerated container (PRNT-4591) — skip the extra container list.
      includeUserGeneratedContainers: false,
    },
  });

  // Catalog photos are no longer fetched on page load; the edit form fetches them on demand. The list
  // renders from each hotel's saved photoUrl.
  return (
    <AccommodationsContext.Provider
      value={{
        componentData,
        eventHandle,
        containerId,
        pageSlug,
        accommodationsData: accommodationsData?.eventByName || undefined,
        travelSettings: accommodationsData?.eventByName?.travelSettings,
        venueData: venueInfo,
        // Catalog photos are now fetched on-demand in the edit form and booking details moved to
        // BookingsTab, so loadingData reflects only the core accommodations + event-place fetches.
        loadingData: loadingAccommodations || loadingEventPlaces,
        isSuperAdmin,
        pageConfig,
        refetchPageContainer,
        pageNote: pageContainerData?.pageContainerBySlug?.note ?? '',
        pageContainerId: pageContainerData?.pageContainerBySlug?.id ?? '',
      }}
    >
      {children}
    </AccommodationsContext.Provider>
  );
};
